import classNames from 'classnames';
import { Tooltip, TooltipProps } from 'react-tippy';
import 'react-tippy/dist/tippy.css';
import React, { useEffect, useRef } from 'react';

interface TooltipHandle {
  hideTooltip?: () => void;
  tippy?: { getPopperElement?: (el: Element) => Element | undefined };
  tooltipDOM?: Element;
}

const activePopovers = new Set<() => void>();

interface PopperModifierData {
  styles: Record<string, string | number>;
  offsets: {
    popper: {
      left: number;
      top: number;
    };
  };
}

const crispModifiers = {
  // Use top/left instead of transform: translate3d() so sub-pixel GPU blur doesn't occur at non-100% zoom
  computeStyle: { gpuAcceleration: false },
  // Round popper offsets to integer pixels before computeStyle applies them
  roundOffsets: {
    order: 849,
    enabled: true,
    fn: (data: PopperModifierData) => {
      data.offsets.popper.left = Math.round(data.offsets.popper.left);
      data.offsets.popper.top = Math.round(data.offsets.popper.top);
      return data;
    },
  },

  position: {
    order: 851,
    enabled: true,
    fn: (data: PopperModifierData) => ({
      ...data,
      styles: {
        ...data.styles,
        left: Math.round(data.offsets.popper.left),
        top: Math.round(data.offsets.popper.top),
      },
    }),
  },
};

type Props = TooltipProps & {
  disableOnScroll?: boolean;
  customClass?: string;
  color?: string;
  visible?: boolean;
  onShow?: (data?: unknown) => void;
  onHide?: (data?: unknown) => void;
  children?: React.ReactNode;
};

const PopOver: React.FC<Props> = (props) => {
  const ref = useRef<TooltipHandle | null>(null);

  const {
    disableOnScroll,
    customClass,
    visible = true,
    children,
    html,
    useContext,
    onShow,
    onHide,
    popperOptions,
    ...rest
  } = props;

  const shouldUseContext = useContext ?? React.isValidElement(html);

  const mergedPopperOptions = {
    ...popperOptions,
    modifiers: { ...crispModifiers, ...popperOptions?.modifiers },
  };

  useEffect(() => {
    if (disableOnScroll) {
      return undefined;
    }

    const onScroll = (event: Event) => {
      const instance = ref.current;
      if (!instance?.hideTooltip) {
        return;
      }

      const popper = instance.tippy?.getPopperElement?.(instance.tooltipDOM);
      if (popper && event.target instanceof Element && popper.contains(event.target)) {
        return;
      }

      instance.hideTooltip();
    };

    window.addEventListener('scroll', onScroll, true);

    return () => {
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [disableOnScroll]);

  const hideSelf = () => {
    ref.current?.hideTooltip?.();
  };

  const handleShow = () => {
    activePopovers.forEach((hide) => hide());
    activePopovers.add(hideSelf);
    onShow?.();
  };

  const handleHide = () => {
    activePopovers.delete(hideSelf);
    onHide?.();
  };

  useEffect(() => {
    return () => {
      activePopovers.delete(hideSelf);
    };
  }, []);

  return (
    <>
      {visible ? (
        React.createElement(
          Tooltip,
          {
            ...(rest as TooltipProps),
            html,
            ref: ref as never,
            useContext: shouldUseContext,
            className: classNames(customClass),
            onShow: handleShow,
            onHide: handleHide,
            popperOptions: mergedPopperOptions,
          },
          children
        )
      ) : (
        <>{children}</>
      )}
    </>
  );
};

export default PopOver;
